// import api's and mutations
import { queries } from 'RestAPIs/rapini';

// import components
import { FormControlLabel, Switch, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';

// import hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// import stores
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';

// import styles
import { StyledBoxFlexRow } from 'components/shared/Box/Box.styles';

// import types
import type { GridColDef } from '@mui/x-data-grid-pro';
import type { TravelDistancesOverviewProps } from './TravelDistancesOverview.types';

// import utils
import { format } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { displayNumber } from 'utils/misc';
import {
  Footer,
  Header
} from 'components/roster-portal/home/travel-expense-claims/components';

dayjs.extend(utc);

const TravelDistancesOverview = ({
  employeeNumber
}: TravelDistancesOverviewProps) => {
  'use memo';
  const { t } = useTranslation();

  const columnProperties: GridColDef[] = [
    {
      field: 'start',
      flex: 1,
      headerName: t(`date`)
    },
    {
      field: 'travelDistance',
      flex: 1,
      headerName: t(`travelDistance`),
      type: 'number'
    },
    {
      field: 'travelDistanceCompensation',
      flex: 1,
      headerName: t(`travelDistanceCompensation`),
      type: 'number'
    },
    {
      field: 'status',
      flex: 1,
      headerName: t(`status`)
    }
  ];

  const { selectedDate, setSelectedDate } = useDateNavigationHoursStore(
    (state) => state
  );
  const startDate = dayjs.utc(selectedDate).startOf('month').toISOString();
  const endDate = dayjs.utc(selectedDate).endOf('month').toISOString();

  const { data: duties, isLoading } = queries.useGetDuties(
    startDate,
    endDate,
    //@ts-expect-error TODO: BE should accept array of ids
    [employeeNumber].join(','),
    true,
    false,
    false,
    true,
    false,
    true,
    false
  );

  const [checked, setChecked] = useState<boolean>(false);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  // Sort duties by start date
  const sortedDuties = duties
    ? duties
        .filter((a) => !dayjs(a.start).isBefore(dayjs(startDate)))
        .sort((a, b) => {
          return new Date(a.start).getTime() - new Date(b.start).getTime();
        })
    : [];

  const filteredDuties = sortedDuties.filter(function (duty) {
    // if checked return all duties, else return only duties with travelDistanceCompensation > 0
    return checked || (duty?.travelDistanceCompensation ?? 0) > 0;
  });

  const rows = filteredDuties.map((duty) => ({
    id: duty.id,
    start: format(new Date(duty.start), 'dd-MM-yyyy'),
    status: t(`dutyStatus.${duty.status}`),
    travelDistance: displayNumber(duty.travelDistance ?? 0, 1),
    travelDistanceCompensation:
      '€' + displayNumber(duty.travelDistanceCompensation ?? 0, 2)
  }));

  const { totalKilometers, totalKilometerCompensation } = (duties ?? [])
    .filter((duty) => duty.status !== 'REJECTED')
    .reduce(
      (acc, duty) => ({
        totalKilometers: acc.totalKilometers + (duty.travelDistance ?? 0),
        totalKilometerCompensation:
          acc.totalKilometerCompensation +
          (duty.travelDistanceCompensation ?? 0)
      }),
      { totalKilometers: 0, totalKilometerCompensation: 0 }
    );

  return (
    <>
      <Header
        onSelectedDate={setSelectedDate}
        selectedDate={dayjs(selectedDate).toDate()}
      />

      <Box sx={{ display: 'flex', justifyContent: 'end' }}>
        <FormControlLabel
          label={t(`showDutiesWithoutKilometers`)}
          labelPlacement="start"
          control={<Switch checked={checked} onChange={handleChange} />}
        />
      </Box>
      <TableCRUDLayout
        loading={isLoading}
        columns={columnProperties}
        rows={rows}
        editable={false}
        footer={
          <Footer>
            <StyledBoxFlexRow>
              <Typography variant="body1">
                {t(`totalKilometers`)}: {displayNumber(totalKilometers, 1)}
              </Typography>
              &bull;
              <Typography variant="body1">
                {`${t(`totalKilometersCompensation`)}: €${displayNumber(
                  totalKilometerCompensation,
                  2
                )}`}
              </Typography>
            </StyledBoxFlexRow>
          </Footer>
        }
      />
    </>
  );
};

export default TravelDistancesOverview;
